import { HTMLProps, ReactNode, RefObject, useLayoutEffect, useRef, useState } from 'react';

const useHasOverflow = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  direction: 'horizontal' | 'vertical',
  callback?: (hasOverflow: boolean) => void,
  elementSelector: (current: T | null) => HTMLElement | null = current => current,
) => {
  const [isOverflow, setIsOverflow] = useState<boolean | undefined>(undefined);

  useLayoutEffect(() => {
    const element = elementSelector(ref.current);
    if (!element) {
      return;
    }
    let hasOverflow = false;

    if (direction === 'horizontal') {
      hasOverflow = element.scrollWidth > element.clientWidth;
    } else {
      hasOverflow = element.scrollHeight > element.clientHeight;
    }

    setIsOverflow(hasOverflow);

    if (callback && isOverflow !== hasOverflow) {
      callback(hasOverflow);
    }
  }, [callback, ref, direction, elementSelector, isOverflow]);

  return isOverflow;
};
type Props<ChildrenElement extends HTMLElement> = {
  onOverflow: (hasOverflow: boolean) => void;
  direction: 'horizontal' | 'vertical';
  render: (ref: RefObject<ChildrenElement | null>) => ReactNode;
} & Omit<HTMLProps<HTMLDivElement>, 'children' | 'onClick'>;

export function OverflowTrigger<ChildrenElement extends HTMLElement>({
  onOverflow,
  direction,
  render,
  ...props
}: Props<ChildrenElement>) {
  const ref = useRef<ChildrenElement>(null);
  useHasOverflow(ref, direction, onOverflow, c => c?.parentElement ?? null);
  return <div {...props}>{render(ref)}</div>;
}
